import React, { useMemo } from 'react';
import { Container, Row, Col, Button, Card, Accordion } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QuickStartIllustration } from '../components/Illustrations';
import { SITE_PUBLIC_NAME } from '../data/legalCompanyInfo';
import PublicSiteNavbar from '../components/PublicSiteNavbar';
import PublicSiteFooter from '../components/PublicSiteFooter';
import { LocalizedLink } from '../components/LocalizedLink';
import { useLocale } from '../contexts/LocaleContext';
import SeoAlternateLinks from '../components/SeoAlternateLinks';
import { SITE_ORIGIN, canonicalUrlForLocale } from '../utils/seoLocale';
import StructuredData from '../components/seo/StructuredData';
import { buildHomepageStructuredGraph } from '../utils/structuredDataSchemas';
import { trackConversionEvent } from '../utils/matomo';
import PublicConversionCtas from '../components/PublicConversionCtas';

const LANDING_PATH = '/';

const LandingPage = () => {
  const { t } = useTranslation(['landing', 'common']);
  const { locale } = useLocale();
  const location = useLocation();
  const canonical = canonicalUrlForLocale(locale, LANDING_PATH);

  const handleRegisterClick = () => {
    trackConversionEvent(location.pathname);
  };

  const structuredData = useMemo(() => {
    const faqItems = t('faq.items', { returnObjects: true }) || [];
    return buildHomepageStructuredGraph({
      faqItems,
      orgDescription: t('jsonLd.orgDesc'),
      canonicalUrl: canonical,
      homeLabel: locale === 'en' ? 'Home' : 'Accueil',
    });
  }, [t, canonical, locale]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: locale === 'en' ? 'en' : 'fr' }}>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:image" content={`${SITE_ORIGIN}/og-image.png`} />
        <meta property="og:site_name" content={SITE_PUBLIC_NAME} />
        <meta property="og:locale" content={locale === 'en' ? 'en_GB' : 'fr_FR'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonical} />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/og-image.png`} />
      </Helmet>
      <StructuredData graph={structuredData} />
      <SeoAlternateLinks pathWithoutLocale={LANDING_PATH} />

      <PublicSiteNavbar />

      <main id="main-content" className="public-site">
      <section className="hero-gradient text-white d-flex align-items-center py-5 py-lg-0">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="text-center text-lg-start mb-4 mb-lg-0">
              <div className="badge bg-light text-primary px-2 px-md-3 py-2 rounded-pill mb-3 fw-semibold hero-badge">
                <i className="bi bi-lightning-charge-fill me-1" />
                <span className="d-none d-sm-inline">{t('hero.badgeLong')}</span>
                <span className="d-inline d-sm-none">{t('hero.badgeShort')}</span>
              </div>
              <h1 className="display-3 fw-bold mb-3 mb-md-4 hero-title">
                {t('hero.titleBefore')}{' '}
                <span className="text-white opacity-75 d-block d-sm-inline">{t('hero.titleHighlight')}</span>
              </h1>
              <p className="lead mb-3 mb-md-4 opacity-90">
                {t('hero.lead')}
                <br className="d-none d-sm-inline" />
                <strong>{t('hero.leadStrong')}</strong>
              </p>

              <div className="mb-3 mb-md-4 justify-content-center justify-content-lg-start">
                <PublicConversionCtas
                  onRegisterClick={handleRegisterClick}
                  sourcePath={location.pathname}
                  className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start"
                  registerVariant="light"
                />
              </div>

              <div className="d-flex flex-wrap gap-3 text-white small justify-content-center justify-content-lg-start">
                {(t('hero.bullets', { returnObjects: true }) || []).map((b) => {
                  const isNoSubscription = b === t('hero.noSubscriptionHighlight');
                  return (
                    <div
                      key={b}
                      className={isNoSubscription ? 'hero-bullet hero-bullet-highlight px-3 py-1 rounded-pill' : undefined}
                    >
                      <i className={`bi ${isNoSubscription ? 'bi-shield-check' : 'bi-check-circle-fill'} text-success me-2`} />
                      {isNoSubscription ? <strong>{b}</strong> : b}
                    </div>
                  );
                })}
              </div>
            </Col>

            <Col lg={6} className="d-none d-lg-block">
              <div className="text-center hero-illustration">
                <QuickStartIllustration width={500} height={400} className="fade-in hero-illustration-img" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="guides" className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-4">
            <Col>
              <h2 className="display-6 fw-bold mb-2">{t('pillars.title')}</h2>
              <p className="text-muted mb-0">{t('pillars.subtitle')}</p>
            </Col>
          </Row>
          <Row className="g-3 justify-content-center">
            {(t('pillars.items', { returnObjects: true }) || []).map((item) => (
              <Col xs={12} lg={4} key={item.to}>
                <Card className="hover-lift h-100">
                  <Card.Body className="d-flex align-items-center">
                    <LocalizedLink to={item.to} className="text-decoration-none fw-semibold">
                      {item.label}
                      <i className="bi bi-arrow-right ms-2" />
                    </LocalizedLink>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="advantages" className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">{t('advantages.title')}</h2>
              <p className="lead text-muted">{t('advantages.subtitle')}</p>
            </Col>
          </Row>

          <Row className="g-4 justify-content-center">
            {(t('advantages.cards', { returnObjects: true }) || []).map((card, i) => (
              <Col xs={12} md={6} lg={4} key={card.h}>
                <Card
                  className="hover-lift h-100 text-center p-3 p-md-4 fade-in"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="feature-icon">
                    <i className={`bi ${['bi-lightning-charge-fill', 'bi-graph-up-arrow', 'bi-heart-fill'][i]}`} />
                  </div>
                  <Card.Body>
                    <h3 className="fw-bold mb-3">{card.h}</h3>
                    <p className="text-muted">
                      {card.p}{' '}
                      <LocalizedLink to={card.linkTo} className="text-decoration-none">
                        {card.link}
                      </LocalizedLink>
                      .
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="features" className="py-5 bg-white">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">{t('features.title')}</h2>
              <p className="lead text-muted">{t('features.subtitle')}</p>
            </Col>
          </Row>

          <Row className="g-4" style={{ counterReset: 'feature-counter' }}>
            {(t('features.items', { returnObjects: true }) || []).map(([title, body], idx) => (
              <Col xs={12} md={6} lg={4} key={title}>
                <Card
                  className="hover-lift h-100 text-center p-3 p-md-4 fade-in"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="feature-icon">
                    <i
                      className={`bi ${
                        [
                          'bi-phone-fill',
                          'bi-shield-lock-fill',
                          'bi-currency-euro',
                          'bi-download',
                          'bi-share-fill',
                          'bi-people-fill',
                          'bi-window-stack',
                          'bi-file-earmark-arrow-up-fill',
                          'bi-chat-dots-fill',
                        ][idx]
                      }`}
                    />
                  </div>
                  <Card.Body>
                    <h3 className="fw-bold mb-3">{title}</h3>
                    <p className="text-muted">{body}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="how-it-works" className="py-5 bg-light position-relative">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">{t('how.title')}</h2>
              <p className="lead text-muted">{t('how.subtitle')}</p>
            </Col>
          </Row>

          <Row className="g-4 position-relative">
            <div className="d-none d-md-block steps-connector">
              <svg className="steps-line" viewBox="0 0 100 20" preserveAspectRatio="none">
                <path d="M 10 10 L 90 10" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" fill="none" opacity="0.3" />
                <circle cx="10" cy="10" r="3" fill="currentColor" opacity="0.5" />
                <circle cx="50" cy="10" r="3" fill="currentColor" opacity="0.5" />
                <circle cx="90" cy="10" r="3" fill="currentColor" opacity="0.5" />
              </svg>
            </div>

            {(t('how.steps', { returnObjects: true }) || []).map(([title, body], idx) => (
              <Col xs={12} md={4} className="position-relative z-1" key={title}>
                <Card className="hover-lift h-100 text-center p-3 p-md-4 step-card fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <Card.Body>
                    <div className="step-number-wrapper mb-4">
                      <div className="step-number">{idx + 1}</div>
                    </div>
                    <h3 className="fw-bold mb-3">{title}</h3>
                    <p className="text-muted">{body}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="faq" className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">{t('faq.title')}</h2>
              <p className="lead text-muted">{t('faq.subtitle')}</p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Accordion defaultActiveKey="0">
                {(t('faq.items', { returnObjects: true }) || []).map((item, i) => (
                  <Accordion.Item eventKey={String(i)} key={item.q}>
                    <Accordion.Header as="h3">
                      <strong>{item.q}</strong>
                    </Accordion.Header>
                    <Accordion.Body style={{ whiteSpace: 'pre-line' }}>{item.a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5 cta-gradient text-white">
        <Container>
          <Row className="text-center">
            <Col>
              <div className="mb-4 cta-emoji">🚀</div>
              <h2 className="display-4 fw-bold mb-4 cta-title">{t('cta.title')}</h2>
              <p className="lead mb-4">{t('cta.lead')}</p>
              <Button
                as={LocalizedLink}
                to="/register/"
                variant="light"
                size="lg"
                className="px-5 py-3 fw-bold text-primary"
                onClick={handleRegisterClick}
              >
                <i className="bi bi-gift-fill me-2" />
                {t('cta.btn')}
              </Button>
              <p className="mt-3 mb-1 fw-semibold">{t('cta.noteLead')}</p>
              <p className="small mb-2">{t('cta.noteDetail')}</p>
              <p className="small mb-0">
                <LocalizedLink to="/tarifs/" className="text-white fw-semibold">
                  {t('cta.pricingLink')}
                </LocalizedLink>
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      </main>

      <PublicSiteFooter />
    </>
  );
};

export default LandingPage;
